import React, { useState, useCallback, useEffect, useRef } from "react";
import { connect } from 'react-redux';
import { Formik } from "formik";

import { Button } from '../../../../components/custom-essentials';
import { SelectSingle, FormikControl, Switch, checkResponses } from '../../../../components/form';
import { Table } from '../../../../components/table';
import { EmployeeModal } from "../../../../components/modal";
import { BrowserTabTitle, LoadingOverlay, TooltipWrapper } from "../../../../components/display";
import { CSVExport } from '../../../../components/export';
import { getColumns } from './table/getColumns';
import { Socket } from '../../../../components/ws';


import {
    fetchAdminUsersAll,
    fetchMpCentersAll,
    fetchRpCentersAll,
} from '../../../../actions';
import { getDataArray } from '../../../../components/functions';

const pageTitle = 'Employee Accounts';

const filterTypeOptions = [
    { value: 'all', label: 'All' },
    { value: 'id', label: 'UUID' },
    { value: 'name', label: 'Name' },
    { value: 'mpCenter', label: 'MP Center' },
    { value: 'rpCenter', label: 'RP Center' },
    { value: 'email', label: 'Email' },
    { value: 'phone', label: 'Phone' },
    { value: 'mpPermissions', label: 'MP Permissions' },
    { value: 'rpPermissions', label: 'RP Permissions' },
];

const defaultSorted = { dataField: 'name', order: 'desc' };

const initialValues = {
    filterType: filterTypeOptions[0],
    filterQuery: '',
    showInactive: false,
};

function Employees(props) {
    const mounted = useRef(false);
    useEffect(() => {
        mounted.current = true;
        return () => mounted.current = false;
    });

    const [loading, setLoading] = useState(false);
    const [apiError, setApiError] = useState(false);
    // Data
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    // Modal
    const [modalMode, setModalMode] = useState(null);
    const [selectedEmployee, setSelectedEmployee] = useState(null);

    // const urlParams = new URLSearchParams(window.location.search);

    const { fetchAdminUsersAll, fetchMpCentersAll, fetchRpCentersAll } = props;

    const filterData = useCallback((users, filterType, filterQuery, showInactive) => {
        const formattedFQ = filterQuery.toLowerCase().trim().replace(/ /g, '');
        const checkId = (u) => u.id.toLowerCase().includes(formattedFQ);
        const checkName = (u) => (`${u.first_name}${u.last_name}`).toLowerCase().replace(/ /g, '').includes(formattedFQ);
        const checkMPCenter = (u) => u.mpCenterName.toLowerCase().replace(/ /g, '').includes(formattedFQ);
        const checkRPCenter = (u) => u.rpCenterName.toLowerCase().replace(/ /g, '').includes(formattedFQ);
        const checkEmail = (u) => u.email.toLowerCase().includes(formattedFQ);
        const checkPhone = (u) => u.phone.toString().includes(formattedFQ);
        const checkMPPermissions = (u) => u.mp_permissions.toLowerCase().includes(formattedFQ);
        const checkRPPermissions = (u) => u.rp_permissions.toLowerCase().includes(formattedFQ);

        const showInactiveTrue = showInactive === 'true' || showInactive === true;

        const newFilteredUsers = users.filter(u => {
            switch (filterType.value) {
                case 'all':
                    return checkId(u) || checkName(u) || checkMPCenter(u) || checkRPCenter(u) ||
                        checkEmail(u) || checkPhone(u) || checkMPPermissions(u) || checkRPPermissions(u);
                case 'id':
                    return checkId(u);
                case 'name':
                    return checkName(u);
                case 'mpCenter':
                    return checkMPCenter(u);
                case 'rpCenter':
                    return checkRPCenter(u);
                case 'email':
                    return checkEmail(u);
                case 'phone':
                    return checkPhone(u);
                case 'mpPermissions':
                    return checkMPPermissions(u);
                case 'rpPermissions':
                    return checkRPPermissions(u);
                default:
                    return false;
            }
        })
            .filter(u => showInactiveTrue || parseInt(u.account_active) === 1);

        if (mounted.current) setFilteredUsers(newFilteredUsers);
    }, [setFilteredUsers, mounted]);
    const refreshData = useCallback((formik) => {
        (async function refresh() {
            if (loading) return;
            if (mounted.current) setLoading(true);

            try {
                // Detect portal from hostname
                const hostname = typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : '';
                const isReadingPlex = hostname.includes('readingplex');

                const adminUsersRes = await fetchAdminUsersAll();
                const mpCentersRes = await fetchMpCentersAll();
                const rpCentersRes = await fetchRpCentersAll();

                // Debug logging - comprehensive response inspection
                console.log('[EMPLOYEES] Raw API Responses:', {
                    adminUsers: {
                        fullResponse: adminUsersRes,
                        status: adminUsersRes?.status,
                        statusText: adminUsersRes?.statusText,
                        hasData: !!adminUsersRes?.data,
                        dataType: typeof adminUsersRes?.data,
                        isArray: Array.isArray(adminUsersRes?.data),
                        dataKeys: adminUsersRes?.data ? Object.keys(adminUsersRes.data) : [],
                        dataValue: adminUsersRes?.data,
                        dataLength: Array.isArray(adminUsersRes?.data) ? adminUsersRes.data.length : 'N/A'
                    },
                    mpCenters: {
                        fullResponse: mpCentersRes,
                        status: mpCentersRes?.status,
                        statusText: mpCentersRes?.statusText,
                        hasData: !!mpCentersRes?.data,
                        dataType: typeof mpCentersRes?.data,
                        isArray: Array.isArray(mpCentersRes?.data),
                        dataKeys: mpCentersRes?.data ? Object.keys(mpCentersRes.data) : [],
                        dataValue: mpCentersRes?.data,
                        dataLength: Array.isArray(mpCentersRes?.data) ? mpCentersRes.data.length : 'N/A'
                    },
                    rpCenters: {
                        fullResponse: rpCentersRes,
                        status: rpCentersRes?.status,
                        statusText: rpCentersRes?.statusText,
                        hasData: !!rpCentersRes?.data,
                        dataType: typeof rpCentersRes?.data,
                        isArray: Array.isArray(rpCentersRes?.data),
                        dataKeys: rpCentersRes?.data ? Object.keys(rpCentersRes.data) : [],
                        dataValue: rpCentersRes?.data,
                        dataLength: Array.isArray(rpCentersRes?.data) ? rpCentersRes.data.length : 'N/A'
                    }
                });

                // Check response validity - make centers optional based on portal
                // On MathPlex portal, rpCenters is optional (404 is acceptable)
                // On ReadingPlex portal, mpCenters is optional (404 is acceptable)
                const requiredResponses = [adminUsersRes];
                
                if (isReadingPlex) {
                    // ReadingPlex: rpCenters required, mpCenters optional
                    // Only add rpCenters if it's successful (< 400) - if it fails, we want checkResponses to catch it
                    if (rpCentersRes && rpCentersRes.status !== undefined) {
                        requiredResponses.push(rpCentersRes);
                    }
                    // mpCenters is optional - don't add to requiredResponses, just warn if error
                    if (mpCentersRes && mpCentersRes.status >= 400) {
                        console.warn('[EMPLOYEES] mpCenters returned error on ReadingPlex portal (optional, continuing):', mpCentersRes.status);
                    }
                } else {
                    // MathPlex: mpCenters required, rpCenters optional
                    // Only add mpCenters if it's successful (< 400) - if it fails, we want checkResponses to catch it
                    if (mpCentersRes && mpCentersRes.status !== undefined) {
                        requiredResponses.push(mpCentersRes);
                    }
                    // rpCenters is optional - don't add to requiredResponses, just warn if error
                    if (rpCentersRes && (rpCentersRes.status === undefined || rpCentersRes.status >= 400)) {
                        console.warn('[EMPLOYEES] rpCenters returned error on MathPlex portal (optional, continuing):', rpCentersRes.status || 'empty response');
                    }
                }

                // Log detailed status before checking
                console.log('[EMPLOYEES] Required responses status check:', {
                    requiredResponsesCount: requiredResponses.length,
                    requiredResponsesStatuses: requiredResponses.map(r => ({ hasResponse: !!r, status: r?.status, statusText: r?.statusText })),
                    adminUsersStatus: adminUsersRes?.status,
                    mpCentersStatus: mpCentersRes?.status,
                    rpCentersStatus: rpCentersRes?.status,
                    isReadingPlex: isReadingPlex
                });

                const isApiError = checkResponses(...requiredResponses);
                if (isApiError) {
                    console.error('[EMPLOYEES] API Error detected by checkResponses:', {
                        requiredResponses: requiredResponses.map(r => ({ hasResponse: !!r, status: r?.status, statusText: r?.statusText })),
                        adminUsers: { response: adminUsersRes, status: adminUsersRes?.status, error: !adminUsersRes || !adminUsersRes.status || adminUsersRes.status >= 400 },
                        mpCenters: { response: mpCentersRes, status: mpCentersRes?.status, error: !mpCentersRes || !mpCentersRes.status || mpCentersRes.status >= 400 },
                        rpCenters: { response: rpCentersRes, status: rpCentersRes?.status, error: !rpCentersRes || !rpCentersRes.status || rpCentersRes.status >= 400 }
                    });
                    if (mounted.current) {
                        setApiError('Error fetching data from the server. Please try again later.');
                        setLoading(false);
                    }
                    return;
                } else {
                    setApiError(false);
                    console.log('[EMPLOYEES] All required responses passed checkResponses validation');
                }

                // Use shared getDataArray utility for robust data extraction
                // Handle optional centers gracefully - use empty array if 404
                const newUsers = getDataArray(adminUsersRes);
                const newMpCenters = (mpCentersRes && mpCentersRes.status < 400) ? getDataArray(mpCentersRes) : [];
                const newRpCenters = (rpCentersRes && rpCentersRes.status < 400) ? getDataArray(rpCentersRes) : [];

                console.log('[EMPLOYEES] Extracted Arrays:', {
                    newUsers: { length: newUsers.length, isArray: Array.isArray(newUsers), sample: newUsers.slice(0, 2) },
                    newMpCenters: { length: newMpCenters.length, isArray: Array.isArray(newMpCenters), sample: newMpCenters.slice(0, 2) },
                    newRpCenters: { length: newRpCenters.length, isArray: Array.isArray(newRpCenters), sample: newRpCenters.slice(0, 2) }
                });

                const mpCenterMap = {};
                mpCenterMap[-1] = 'None';
                const rpCenterMap = {};
                rpCenterMap[-1] = 'None';
                if (Array.isArray(newMpCenters)) {
                    newMpCenters.forEach(c => mpCenterMap[parseInt(c.id)] = c.name);
                }
                if (Array.isArray(newRpCenters)) {
                    newRpCenters.forEach(c => rpCenterMap[parseInt(c.id)] = c.name);
                }

                if (Array.isArray(newUsers)) {
                    newUsers.forEach(u => {
                        u.mpCenterName = mpCenterMap[u.mp_primary_center] || `Unable to find center (ID :${u.mp_primary_center})`;
                        u.rpCenterName = rpCenterMap[u.rp_primary_center] || `Unable to find center (ID :${u.rp_primary_center})`;
                    });
                }

                const { filterType, filterQuery, showInactive } = formik.values;
                if (mounted.current) {
                    setUsers(newUsers);
                    filterData(newUsers, filterType, filterQuery, showInactive);
                    setLoading(false);
                }
            } catch (error) {
                console.error('[EMPLOYEES] Unexpected error:', error);
                if (mounted.current) {
                    setApiError('An unexpected error occurred. Please try again later.');
                    setLoading(false);
                }
            }
        })();
    }, [fetchAdminUsersAll, fetchMpCentersAll, fetchRpCentersAll, loading, mounted, setUsers, setLoading, filterData]);
    useEffect(() => {
        refreshData({ values: initialValues });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const columns = getColumns(handleShowModal, props.auth.permissions);
    function handleShowModal(mode, user) {
        setModalMode(mode);
        setSelectedEmployee(user);
    }
    function onSubmitCallback(formik, changes = true) {
        setModalMode(null);
        setSelectedEmployee(null);
        if (changes) refreshData(formik);
    }

    return (
        <div className="page-box">
            <BrowserTabTitle>{pageTitle}</BrowserTabTitle>
            {loading && <LoadingOverlay />}
            <div className="card">
                <h2>Employee Accounts ({filteredUsers.length})</h2>
                <br />
                <Formik
                    enableReinitialize
                    initialValues={initialValues}
                    handleSubmit={() => null}
                >
                    {formik => (
                        <>
                            <div className="flex flex-row gap-x-4">
                                <div className="grid grid-cols-1 gap-y-2 w-1/6">
                                    <SelectSingle
                                        id="employees-type-1"
                                        value={formik.values.filterType}
                                        name="filterType"
                                        label="Filter Type"
                                        onChange={(e) => {
                                            formik.handleChange(e);
                                            filterData(users, e.target.value,
                                                formik.values.filterQuery, formik.values.showInactive);
                                        }}
                                        options={filterTypeOptions}
                                    />
                                </div>
                                <div className="grid grid-cols-1 gap-y-2 w-1/4">
                                    <FormikControl
                                        id="employees-query-1"
                                        name="filterQuery"
                                        placeholder="Enter a filter query..."
                                        value={formik.values.filterQuery}
                                        onChange={(e) => {
                                            formik.handleChange(e);
                                            filterData(users, formik.values.filterType,
                                                e.target.value, formik.values.showInactive);
                                        }}
                                    />
                                </div>
                                <div className="grid grid-cols-1 gap-y-2">
                                    <Switch
                                        id="employees-show-inactive"
                                        name="showInactive"
                                        label="Show Inactive"
                                        color="mpLRed"
                                        checked={formik.values.showInactive}
                                        onChange={(e) => {
                                            formik.handleChange(e);
                                            filterData(users, formik.values.filterType,
                                                formik.values.filterQuery, e.target.value);
                                        }}
                                    />
                                </div>
                                <div className="grid grid-cols-1 gap-y-2">
                                    <Button
                                        color="lte-mpLBlue"
                                        onClick={() => setModalMode('create')}
                                    >
                                        + Add Employee
                                    </Button>
                                </div>
                                <div className="ml-auto">
                                    <TooltipWrapper
                                        tooltipText={
                                            <div>
                                                <div>
                                                    What gets exported?
                                                </div>
                                                <br />
                                                <div>
                                                    All users that are currently filtered ({filteredUsers.length} items).
                                                </div>
                                            </div>
                                        }
                                    >
                                        <CSVExport
                                            title="Employees"
                                            label="Export users to CSV"
                                            data={filteredUsers}
                                        />
                                    </TooltipWrapper>
                                </div>
                            </div>
                            {modalMode && <EmployeeModal
                                mode={modalMode}
                                selectedEmployee={selectedEmployee}
                                onSubmitCallback={(changes) => onSubmitCallback(formik, changes)}
                            />}
                            <Socket
                                refreshData={() => refreshData(formik)}
                                page={pageTitle}
                                setVersion={props.setVersion}
                            />
                        </>
                    )}
                </Formik>

                <br />

                {apiError ? <div className="text-mpLRed">{apiError}</div> :
                    <Table
                        tableName="employees"
                        data={filteredUsers}
                        columns={columns}
                        defaultSorted={defaultSorted}
                        sizePerPageInitial={50}
                        emptyMessage="No employees were found"
                    />
                }
            </div>
        </div>
    );
};

const mapStateToProps = (state) => {
    return {
        auth: state.auth
    };
}

export default connect(mapStateToProps, {
    fetchAdminUsersAll,
    fetchMpCentersAll,
    fetchRpCentersAll,
})(Employees);